import { Button, Spinner } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { Landmark } from 'lucide-react';
import { useCallback, useState } from 'react';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { PageHeader } from '@/components/layout/page-header';
import { AddInstitutionDialog } from '@/features/credit-lines/add-institution-dialog';
import { LoanCard } from '@/features/credit-lines/loan-card';
import { UnauthorisedActionDialog } from '@/features/credit-lines/unauthorised-action-dialog';
import { LOANS_QUERY_KEY, useLoans } from '@/hooks/queries/system/use-loans';
import { useQueryErrorToast } from '@/hooks/use-query-error-toast';
import { getErrorMessage } from '@/lib/api/error';
import { formatEuros } from '@/utils/format';

const FETCH_ERROR_TOAST_ID = 'credit-lines-fetch-error';

// Page title "Loans"; one full-width card per institution.
const CreditLinesPage = () => {
  const queryClient = useQueryClient();
  const loansQuery = useLoans();
  const { data, isPending, isError, error, refetch } = loansQuery;
  const [isAddInstitutionOpen, setIsAddInstitutionOpen] = useState(false);
  const [isUnauthorisedOpen, setIsUnauthorisedOpen] = useState(false);

  useQueryErrorToast(loansQuery, 'Failed to fetch credit lines', FETCH_ERROR_TOAST_ID);

  // updateLoan is a full-state upsert with last-writer-wins — refetch after
  // every mutation so the next payload is always built from fresh data.
  const handleMutated = useCallback(() => queryClient.invalidateQueries({ queryKey: LOANS_QUERY_KEY }), [queryClient]);
  const handleUnauthorised = useCallback(() => setIsUnauthorisedOpen(true), []);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader documentTitle="Loans - Bespoke Ops Portal - One Trading" title="Loans" />

      {isPending ? (
        // Full-page loading panel while the GET is in flight (cards stay
        // mounted on later background refetches — data never blanks)
        <div className="flex h-64 items-center justify-center">
          <Spinner className="size-8" />
        </div>
      ) : null}

      {isError ? <QueryErrorBanner message={`Failed to fetch credit lines: ${getErrorMessage(error)}`} onRetry={() => refetch()} /> : null}

      {data ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-xl font-semibold tabular-nums">Global credit lines total is {formatEuros(data.totalEuroAmount)}</h2>
            <Button className="gap-2" onClick={() => setIsAddInstitutionOpen(true)} type="button">
              <Landmark className="size-4" />
              Add Institution
            </Button>
          </div>

          {data.loans.length === 0 ? (
            // An unseeded environment 404s ('No reserved balances found') —
            // an empty state, not an error; Add Institution bootstraps the record
            <div className="rounded-md border border-dashed px-6 py-12 text-center text-muted-foreground">
              No institutions yet — use Add Institution to create the first credit line.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {data.loans.map((client) => (
                <LoanCard client={client} key={client.accountId} onMutated={handleMutated} onUnauthorised={handleUnauthorised} />
              ))}
            </div>
          )}
        </>
      ) : null}

      {isAddInstitutionOpen ? <AddInstitutionDialog onClose={() => setIsAddInstitutionOpen(false)} onMutated={handleMutated} onUnauthorised={handleUnauthorised} /> : null}
      {isUnauthorisedOpen ? <UnauthorisedActionDialog onClose={() => setIsUnauthorisedOpen(false)} /> : null}
    </div>
  );
};
CreditLinesPage.displayName = 'CreditLinesPage';

export default CreditLinesPage;
